import { useMemo } from 'react';
import { Dropdown } from 'antd';
import { MoreOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import type { IComponentSchema } from '../../types';
import ComponentRenderer from '../../ComponentRenderer';
import { parseJSONSchema, parseTemplateProps } from '../../utils/data-parser';
import { useTranslate } from '../../../translate';
import type { IChildCollection, ITreeNode } from './utils';
import { stripToBeDeletedDeep } from './utils';

interface IRecursionNodeProps {
  node: ITreeNode;
  depth: number;
  customComponentSchema?: IComponentSchema<any> | null;
  context?: Record<string, unknown>;
  canEdit: { value: boolean; config: any };
  canDelete: { value: boolean; config: any };
  canAddChild: { value: boolean; config: any };
  childCollections?: IChildCollection[];
  disabled?: boolean;
  style: { value: any; config: any };
  addItemButtonText?: string;
  addNestedItemButtonText?: string;
  editItemButtonText?: string;
  deleteItemButtonText?: string;
  onAddChild: (item: Record<string, unknown>, title: string) => void;
  onEdit: (item: Record<string, unknown>, title: string) => void;
  onDelete: (item: Record<string, unknown>) => void;
  onAddCollectionRow: (
    item: Record<string, unknown>,
    collection: IChildCollection
  ) => void;
  onEditCollectionRow: (node: ITreeNode) => void;
  onDeleteCollectionRow: (node: ITreeNode) => void;
}

const resolveValue = (
  can: { value: any; config: any },
  node: ITreeNode,
  context: Record<string, unknown>
) => {
  if (typeof can?.value === 'boolean') return can?.value;
  const value = parseTemplateProps({
    componentPropKey: 'canAddChild',
    componentPropValue: can?.value,
    data: { ...context, item: node.item },
    config: can?.config,
    options: {},
  });
  return value;
};

const RecursionNode: React.FC<IRecursionNodeProps> = ({
  node,
  depth,
  customComponentSchema,
  context,
  canEdit,
  canDelete,
  canAddChild,
  childCollections = [],
  disabled = false,
  onAddChild,
  onEdit,
  onDelete,
  onAddCollectionRow,
  onEditCollectionRow,
  onDeleteCollectionRow,
  style: styleRaw,
  addItemButtonText,
  addNestedItemButtonText,
  editItemButtonText,
  deleteItemButtonText,
}) => {
  const { t } = useTranslate();
  const { item, children, collection } = node;

  const { value: canAddChildValue, config: canAddChildConfig } =
    canAddChild || {};
  const { value: canEditValue, config: canEditConfig } = canEdit || {};
  const { value: canDeleteValue, config: canDeleteConfig } = canDelete || {};

  const { canAddItem, canEditItem, canDeleteItem } = useMemo(() => {
    return {
      canAddItem: resolveValue(canAddChild, node, context || {}),
      canEditItem: resolveValue(canEdit, node, context || {}),
      canDeleteItem: resolveValue(canDelete, node, context || {}),
    };
  }, [
    canAddChildValue,
    canAddChildConfig,
    canEditValue,
    canEditConfig,
    canDeleteValue,
    canDeleteConfig,
    node,
    context,
  ]);

  const style = useMemo(() => {
    if (collection) return collection.style || {};
    if (!styleRaw?.value) return {};
    const values: any = {};
    Object.keys(styleRaw.value).forEach(key => {
      if (styleRaw.value[key] !== '"Condition Logic"') {
        values[key] = styleRaw.value[key];
      }
      values[key] = parseTemplateProps({
        componentPropKey: 'key',
        componentPropValue: values[key],
        data: { ...context, item: node.item },
        config: styleRaw?.config ? styleRaw?.config[key] || {} : {},
        options: {},
      });
    });

    return values;
  }, [styleRaw, context, node, collection]);

  const collectionMenuItems = collection
    ? [
        ...(collection.canEdit !== false && !disabled
          ? [
              {
                key: 'edit-row',
                label: editItemButtonText || 'Edit',
                onClick: () => onEditCollectionRow(node),
              },
            ]
          : []),
        ...(collection.canDelete !== false && !disabled
          ? [
              {
                key: 'delete-row',
                label: deleteItemButtonText || 'Delete',
                onClick: () => onDeleteCollectionRow(node),
              },
            ]
          : []),
      ]
    : [];

  const addChildLabel =
    (depth > 0 ? addNestedItemButtonText : undefined) ||
    addItemButtonText ||
    'Add Child';
  const editLabel = editItemButtonText || 'Edit';

  const nodeMenuItems = [
    ...(canAddItem && !disabled
      ? [
          {
            key: 'add-child',
            label: addChildLabel,
            onClick: () => onAddChild(item, addChildLabel),
          },
        ]
      : []),
    ...(disabled
      ? []
      : childCollections.map(childCollection => ({
          key: `add-${childCollection.key}`,
          label: childCollection.label || childCollection.key,
          onClick: () => onAddCollectionRow(item, childCollection),
        }))),
    ...(canEditItem && !disabled
      ? [
          {
            key: 'edit',
            label: editLabel,
            onClick: () => onEdit(item, editLabel),
          },
        ]
      : []),
    ...(canDeleteItem && !disabled
      ? [
          {
            key: 'delete',
            label: deleteItemButtonText || 'Delete',
            onClick: () => onDelete(item),
          },
        ]
      : []),
  ];

  const menuItems = collection ? collectionMenuItems : nodeMenuItems;

  const cardSchema = collection ? collection.cardSchema : customComponentSchema;

  const nodeContent = cardSchema ? (
    (() => {
      // Display-only: hide nested rows flagged for deletion (e.g. a removed
      // shelf-life entry). The tombstoned items stay in the field value for the
      // submit diff — only the rendered copy is cleaned.
      const contextData = { ...context, item: stripToBeDeletedDeep(item) };
      const parsedSchema = parseJSONSchema(cardSchema, contextData, {
        t,
      });
      return (
        <ComponentRenderer schema={parsedSchema} parentContext={contextData} />
      );
    })()
  ) : (
    <NodeLabel>{String(item.id ?? '')}</NodeLabel>
  );

  return (
    <NodeCard $depth={depth} style={style}>
      {!disabled && menuItems.length > 0 && (
        <DropdownWrapper>
          <Dropdown menu={{ items: menuItems }} trigger={['click']}>
            <MenuButton onClick={e => e.stopPropagation()}>
              <MoreOutlined />
            </MenuButton>
          </Dropdown>
        </DropdownWrapper>
      )}
      <NodeHeader>
        <NodeContent>{nodeContent}</NodeContent>
      </NodeHeader>
      {children.length > 0 && (
        <ChildrenContainer>
          {children.map(child => (
            <RecursionNode
              key={child.item.id as string}
              node={child}
              depth={depth + 1}
              customComponentSchema={customComponentSchema}
              context={context}
              canEdit={canEdit}
              canDelete={canDelete}
              canAddChild={canAddChild}
              childCollections={childCollections}
              disabled={disabled}
              onAddChild={onAddChild}
              onEdit={onEdit}
              onDelete={onDelete}
              onAddCollectionRow={onAddCollectionRow}
              onEditCollectionRow={onEditCollectionRow}
              onDeleteCollectionRow={onDeleteCollectionRow}
              style={styleRaw}
              addItemButtonText={addItemButtonText}
              addNestedItemButtonText={addNestedItemButtonText}
              editItemButtonText={editItemButtonText}
              deleteItemButtonText={deleteItemButtonText}
            />
          ))}
        </ChildrenContainer>
      )}
    </NodeCard>
  );
};

export default RecursionNode;

const NodeCard = styled.div<{ $depth: number }>`
  margin: 0 16px;
  margin-left: ${({ $depth }) => ($depth > 0 ? 16 : 0)}px;
  border: 1px solid #f0f0f0;
  border-left: 3px solid #1890ff;
  border-radius: 4px;
  margin-bottom: 4px;
  background: #fff;
  position: relative;
`;

const NodeHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 16px;
  gap: 8px;
`;

const NodeContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const NodeLabel = styled.span`
  color: #262626;
  font-size: 14px;
`;

const MenuButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  color: #8c8c8c;
  font-size: 16px;
  display: flex;
  align-items: center;
  border-radius: 4px;
  flex-shrink: 0;

  &:hover {
    background: #f5f5f5;
    color: #262626;
  }
`;

const DropdownWrapper = styled.div`
  position: absolute;
  right: 8px;
  top: 8px;
`;

const ChildrenContainer = styled.div`
  padding: 0 0 4px 0;
`;
